import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScrollProgress from '../components/ScrollProgress';
import { useLanguage } from '../contexts/LanguageContext';
import { mockData } from '../data/mock';
import { Calendar, Clock, MapPin, Users, ExternalLink } from 'lucide-react';

const CoursesPage = () => {
  const { t, language } = useLanguage();

  return (
    <>
      <ScrollProgress />
      <Header />
      <main className="pt-24 pb-16">
        {/* Hero */}
        <section className="bg-gradient-hero py-16">
          <div className="container-custom">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
                {t('courses.title')}
              </h1>
              <p className="text-xl text-neutral-600">
                {t('courses.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Course Info Cards */}
        <section className="py-12 bg-white">
          <div className="container-custom">
            <div className="grid md:grid-cols-4 gap-6 mb-12">
              <div className="flex items-center gap-4 bg-neutral-50 p-4 rounded-xl">
                <div className="p-3 bg-red-100 rounded-lg">
                  <Calendar className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500">
                    {language === 'de' ? 'Kurstag' : 'Course Day'}
                  </p>
                  <p className="font-semibold text-neutral-900">
                    {t('courseTimes.day')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-neutral-50 p-4 rounded-xl">
                <div className="p-3 bg-red-100 rounded-lg">
                  <Clock className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500">
                    {language === 'de' ? 'Uhrzeit' : 'Time'}
                  </p>
                  <p className="font-semibold text-neutral-900">
                    09:00 - 16:30
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-neutral-50 p-4 rounded-xl">
                <div className="p-3 bg-red-100 rounded-lg">
                  <MapPin className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500">
                    {language === 'de' ? 'Standort' : 'Location'}
                  </p>
                  <p className="font-semibold text-neutral-900">
                    Düsseldorf
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-neutral-50 p-4 rounded-xl">
                <div className="p-3 bg-red-100 rounded-lg">
                  <Users className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500">
                    {language === 'de' ? 'Plätze' : 'Seats'}
                  </p>
                  <p className="font-semibold text-neutral-900">
                    max. 20
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Booking iFrame */}
        <section className="py-8 bg-neutral-50">
          <div className="container-custom">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-red-600 text-white px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold">
                  {language === 'de' ? 'Verfügbare Termine' : 'Available Dates'}
                </h2>
                <a
                  href={mockData.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
                >
                  <span>{language === 'de' ? 'In neuem Tab öffnen' : 'Open in new tab'}</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <div className="booking-iframe-container" style={{ minHeight: '800px' }}>
                <iframe
                  src={mockData.bookingUrl}
                  title="Kursbuchung"
                  className="w-full h-full"
                  style={{ minHeight: '800px' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Additional Info */}
        <section className="py-12 bg-white">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-2xl font-bold text-neutral-900 mb-4">
                {language === 'de' ? 'Hinweise zur Buchung' : 'Booking Information'}
              </h3>
              <div className="text-left bg-neutral-50 rounded-xl p-6 space-y-4">
                <p className="text-neutral-700">
                  {language === 'de'
                    ? '• Eine Online-Voranmeldung ist empfohlen, aber nicht zwingend erforderlich'
                    : '• Online pre-registration is recommended but not mandatory'}
                </p>
                <p className="text-neutral-700">
                  {language === 'de'
                    ? '• Spontane Teilnahme ist möglich, sofern noch Plätze frei sind'
                    : '• Spontaneous participation is possible if seats are available'}
                </p>
                <p className="text-neutral-700">
                  {language === 'de'
                    ? '• Bringen Sie einen gültigen Ausweis oder Reisepass mit'
                    : '• Please bring a valid ID or passport'}
                </p>
                <p className="text-neutral-700">
                  {language === 'de'
                    ? '• Zahlung erfolgt bar vor Ort'
                    : '• Payment is made in cash on site'}
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default CoursesPage;
